import { Injectable, Injector } from "@angular/core";
import { ContentPipe } from "src/app/services/content/content.pipe";
import { DropdownOption } from "src/app/shared/mine-dropdown/mine-dropdown.interface";
import { AddItemConfig } from "src/app/shared/mine-chips-input/mine-chips-input.interface";
import { AssessmentQuestionTypeEnum } from "src/app/api/models/assessments/assessment.enum";
import { AssessmentLogicOperandEnum, AssessmentLogicTypeEnum } from "../models/assessments.enum";
import { AssessmentCustomFieldDropdown, AssessmentCustomFieldText, AssessmentDataTypeSource, AssessmentDropdownOption, AssessmentEntity, AssessmentResourceId, AssessmentResourceValue, PartiesWithAccess } from "src/app/api/models/assessments/assessment.interface";
import { AssessmentLogic, AssessmentLogicCondition, AssessmentLogicConditions, AssessmentPage, AssessmentQuestion, AssessmentSection, AssessmentsMineOSFormControl } from "../models/assessments.interface";
import { AiAssessmentsQuestionTypeHelper } from "../shared/question-type-helper";
import {
    generateControlUuid as coreGenerateControlUuid,
    generateSectionUuid as coreGenerateSectionUuid,
    AssessmentsMineOSFormControl as CoreFormControl,
} from "@mineos/assessment-core";
import { AssessmentsMapperService } from "./assessments-mapper.service";
import { CustomFieldsV2Service } from "src/app/company-settings/custom-fields-v2/custom-fields-v2.service";
import { SystemsQuery } from "src/app/systems/state/systems.query";
import { Risk } from "src/app/risks/models/risks.interface";

import _ from 'lodash';
@Injectable({
    providedIn: 'root'
})
export class AssessmentsLogicManagerService {
    private readonly idDelimiter = '||';

    constructor(
        private injector: Injector,
        private contentPipe: ContentPipe, 
        private questionTypeHelper: AiAssessmentsQuestionTypeHelper,
        private assessmentsMapperService: AssessmentsMapperService,
        private customFieldsV2Service: CustomFieldsV2Service,
    ) {}

    private resolveEntityName(id: string): string {
        return this.customFieldsV2Service.getOption(id)?.text ?? id;
    }

    // Deterministic id generation is owned by @mineos/assessment-core (shared with
    // assessment-generator-v4). These delegate so the two projects can never drift.
    generateControlUuid(control: AssessmentQuestion | AssessmentsMineOSFormControl): string {
        return coreGenerateControlUuid(control as unknown as CoreFormControl);
    }

    generateSectionUuid(section: AssessmentSection | AssessmentsMineOSFormControl): string {
        return coreGenerateSectionUuid(section as unknown as CoreFormControl);
    }

    generateSectionsUuid(sections: AssessmentSection[] | AssessmentsMineOSFormControl[]): AssessmentSection[] | AssessmentsMineOSFormControl[] {
        return sections.map(section => {
            const questions = this.questionTypeHelper.getSectionQuestions(section);
            return {
                ...section,
                id: section.id ?? this.generateSectionUuid(section),
                controls: questions?.map(control => {
                    const controlId = control.hasOwnProperty('templateQuestionId') ? (control as AssessmentQuestion).templateQuestionId : control.id;
                    return {
                        ...control,
                        id: controlId ?? this.generateControlUuid(control),
                    };
                })
            };
        });
    }

    getQuestionById(sections: AssessmentSection[] | AssessmentsMineOSFormControl[], questionId: AssessmentResourceId): AssessmentQuestion | AssessmentSection | AssessmentsMineOSFormControl {
        if (!sections?.length || !questionId) return null;
        for (let section of sections) {
            if ((''+questionId).includes(this.idDelimiter)) {
                const questions = this.questionTypeHelper.getSectionQuestions(section);
                if (questions?.length) {
                    for (let control of questions) {
                        const sectionId = section.hasOwnProperty('templateSectionId') ? (section as AssessmentSection).templateSectionId : section.id;
                        const controlId = control.hasOwnProperty('templateQuestionId') ? (control as AssessmentQuestion).templateQuestionId : control.id;
                        
                        if (this.getSectionControlCombinedId(sectionId, controlId) === questionId) {
                            return {sectionId, ...control};
                        }
                    }
                }
            }
            else if (questionId === section.id) {
                return section;
            }
        }
        return null;
    }

    isValidQuestionId(sections: AssessmentSection[] | AssessmentsMineOSFormControl[], questionId: AssessmentResourceId): boolean {
        if (!sections?.length || !questionId) return false;
        for (let section of sections) {
            if ((''+questionId).includes(this.idDelimiter)) {
                const questions = this.questionTypeHelper.getSectionQuestions(section);
                if (questions?.length) {
                    for (let control of questions) {
                        const sectionId = section.hasOwnProperty('templateSectionId') ? (section as AssessmentSection).templateSectionId : section.id;
                        const controlId = control.hasOwnProperty('templateQuestionId') ? (control as AssessmentQuestion).templateQuestionId : control.id;
                        if (this.getSectionControlCombinedId(sectionId, controlId) === questionId) {
                            return true;
                        }
                    }
                }
            }
            else if (sections.map(section => section.templateSectionId).includes(questionId) || sections.map(section => section.id).includes(questionId)) {
                return true;
            }
        }
        return false;
    }

    getQuestionAddItemConfig(control: AssessmentQuestion | AssessmentsMineOSFormControl, options?: DropdownOption[]): AddItemConfig {
        if (!control) return null;
        let dropDownOptions: DropdownOption[] = [];
        const questionType = this.assessmentsMapperService.getQuestionType({ type: control.type, name: control.name });

        if (questionType === AssessmentQuestionTypeEnum.Dropdown) {
            if (control.hasOwnProperty('layout') && control.hasOwnProperty('value')) {
                dropDownOptions = (control.value as AssessmentDropdownOption[])?.map(option => ({id: option.id, value: option.label} as DropdownOption));
            }
            else {
                dropDownOptions = (control as AssessmentsMineOSFormControl).options?.filter(option => !!option.id && !!option.value);
            }
        }
        else if (!!options) {
            dropDownOptions = options?.filter(option => !!option.id && !!option.value);
        }
        return {
            dropDownOptions,
            inputPlaceHolder: this.contentPipe.transform('ropa.searchInputPlaceholder'),
            fixedOptions: true,
            hasCustomOption: false,
            width: '409px',
        } as AddItemConfig;
    }

    getQuestionRulesCount(currentLogic: AssessmentLogic, key: string): number {
        if (!currentLogic || !Object.keys(currentLogic)?.length || !key) return null;

        return currentLogic[key]?.conditions?.filter(condition => !!condition["questionId"])?.length ?? null;
    }

    getSectionRulesCount(currentLogic: AssessmentLogic, sectionId: AssessmentResourceId): number {
        if (!currentLogic || !Object.keys(currentLogic)?.length || !sectionId) return null;

        return currentLogic[sectionId]?.conditions?.filter(condition => !!condition["questionId"])?.length ?? null;
    }

    canQuestionUseLogic(
        sections: AssessmentSection[] | AssessmentsMineOSFormControl[], 
        sectionIndex: number, 
        sectionId: AssessmentResourceId, 
        controlIndex: number, 
        controlId: AssessmentResourceId
    ): boolean {
        if (!sections?.length || !sectionId || !controlId || !this.isValidIndex(sectionIndex) || !this.isValidIndex(controlIndex)) return false;

        const section = sections[sectionIndex];
        const questions = this.questionTypeHelper.getSectionQuestions(section);
        const control = questions[controlIndex];

        if (control) {
            return !!sections.find((sec, index) => this.questionTypeHelper.getSectionQuestions(sec)?.some((ctrl, i) => {
                const check1 = ctrl.id === control.id;
                const check2 = sectionIndex < index;
                const check3 = sectionIndex === index && i < controlIndex && this.isControlTypeAndNameIsLogicSupported(ctrl);
                const check4 = sectionIndex > index && this.isControlTypeAndNameIsLogicSupported(ctrl);
               
                if (check1) return false;
                if (check2) return false;
                if (check3) return true;
                if (check4) return true;
            }));
        }
        return false;
    }

    canSectionUseLogic(
        sections: AssessmentSection[] | AssessmentsMineOSFormControl[], 
        sectionIndex: number, 
        sectionId: AssessmentResourceId, 
    ): boolean {
        if (!sections?.length || !sectionId || !this.isValidIndex(sectionIndex)) return false;

        const section = sections[sectionIndex];

        if (section) {
            return !!sections.find((sec, index) => this.questionTypeHelper.getSectionQuestions(sec)?.some((ctrl, i) => {
                const check1 = sectionIndex < index;
                const check2 = sectionIndex > index && this.isControlTypeAndNameIsLogicSupported(ctrl);
               
                if (check1) return false;
                if (check2) return true;
            }));
        }
        return false;
    }

    isQuestionUsedInOtherLogic(
        currentLogic: AssessmentLogic, 
        sections: AssessmentSection[] | AssessmentsMineOSFormControl[], 
        sectionId: AssessmentResourceId, 
        controlId: AssessmentResourceId, 
        deletedValue?: string[]
    ): boolean {
        if (!currentLogic || !Object.keys(currentLogic)?.length || !sections?.length || !sectionId || !controlId) return false;
        try {
            for (let questionId of Object.keys(currentLogic)) {
                if (!this.getQuestionById(sections, questionId)) return false;

                const questionLogic = currentLogic[questionId];

                for (let i=0; i<questionLogic.conditions?.length; i++) {
                    const condition = questionLogic.conditions[i];
                    const selectedQuestionId = this.isDropdownOption(condition?.['questionId']) ? condition?.['questionId'].id : condition?.['questionId'];
                    
                    if (selectedQuestionId === this.getSectionControlCombinedId(sectionId, controlId)) {
                        if (!!deletedValue?.length) {
                            const questionValue: Array<any> = Array.isArray(condition?.['questionValue']) ? condition?.['questionValue'] : condition?.['questionValue'].value?.split(',') ?? [];
                            if (this.hasExactly(questionValue, deletedValue)) return true;
                        }
                        else {
                            return true;
                        }
                    }
                }
            }
            return false;
        }
        catch (err) {
            console.error('Could not parse instance logic');
        }
    }

    setQuestionLogic(currentLogic: AssessmentLogic, key: string, newConditionLogic: AssessmentLogicConditions): AssessmentLogic {
        if (!key) return currentLogic;
        try {            
            let updatedLogic: AssessmentLogic = currentLogic;
            if (!currentLogic ||!Object.keys(currentLogic)?.length) {
                updatedLogic = {} as AssessmentLogic;
            }
            if (!!updatedLogic[key] && !newConditionLogic) {
                delete updatedLogic[key];
            }
            else if (!!newConditionLogic) {
                updatedLogic[key] = newConditionLogic;
            }
    
            return updatedLogic;
        }
        catch (err) {
            console.error('Could not parse instance logic');
            return currentLogic;
        }
    }
    
    updateQuestionLogic(currentLogic: AssessmentLogic, key: string, field: keyof AssessmentLogicCondition, update: DropdownOption | string[], conditionIndex: number = 0): AssessmentLogic {
        if (!currentLogic || !Object.keys(currentLogic)?.length || !key || !field || !update) return currentLogic;
        try {            
            let updatedLogic: AssessmentLogic = currentLogic;
            const questionLogicConditions = updatedLogic[key].conditions;

            switch (field) {
                case "type":
                    questionLogicConditions[conditionIndex]["type"] = (update as DropdownOption).id;
                    break;

                case "questionId":
                    questionLogicConditions[conditionIndex]["questionId"] = (update as DropdownOption).id;
                    break;

                case "questionValue":
                    questionLogicConditions[conditionIndex]["questionValue"] = (update as string[]);
                    if (!questionLogicConditions[conditionIndex]["type"]) { // default value in case type is not set
                        questionLogicConditions[conditionIndex]["type"] = AssessmentLogicTypeEnum.HasExactly;
                    }
                    break;
            }
    
            return updatedLogic;
        }
        catch (err) {
            console.error('Could not parse instance logic');
            return currentLogic;
        }
    }

    updateOperand(currentLogic: AssessmentLogic, key: string, operand: AssessmentLogicOperandEnum): AssessmentLogic {
        if (!currentLogic || !Object.keys(currentLogic)?.length || !key || !operand) return currentLogic;
        try {            
            let updatedLogic: AssessmentLogic = currentLogic;
            
            // Ensure the logic entry exists and has the right structure
            if (!updatedLogic[key]) {
                updatedLogic[key] = { conditions: [] as any };
            }
            
            updatedLogic[key].operand = operand;    
            return updatedLogic;
        }
        catch (err) {
            console.error('Could not parse instance logic');
            return currentLogic;
        }
    }

    getQuestionLogicOptions(
        sections: AssessmentSection[] | AssessmentsMineOSFormControl[], 
        sectionIndex: number, 
        sectionId: AssessmentResourceId, 
        controlIndex?: number, 
        controlId?: AssessmentResourceId
    ): DropdownOption[] {
        if (!sections?.length || !sectionId || !this.isValidIndex(sectionIndex)) return [];
        if (controlIndex !== undefined && !this.isValidIndex(controlIndex)) return [];
        try {
            const options = [] as DropdownOption[];

            for (let index=0; index<sections.length; index++) {
                const sec = sections[index];
                const questions = this.questionTypeHelper.getSectionQuestions(sec);

                for (let i=0; i<questions?.length; i++) {
                    const ctrl = questions[i];

                    if (this.isControlTypeAndNameIsLogicSupported(ctrl) && (ctrl.id !== controlId)) {
                        if (((sectionIndex === index) && (i < controlIndex)) || (sectionIndex > index)) {
                            const questionId = this.getSectionControlCombinedId(sec.id, ctrl.id);

                            options.push({
                                id: questionId as string,
                                value: `${index+1}.${(i+1)} ${ctrl.label}`
                            });
                        }
                    }
                }
            }
            return options;
        }
        catch (err) {
            console.error('Could not parse instance logic');
            return [];
        }
    }

    getOperandOptions(): DropdownOption[] {
        return [
            { 
                id: AssessmentLogicOperandEnum.OR, 
                value: this.contentPipe.transform('ai-assessments.logicAnyRule') 
            },
            { 
                id: AssessmentLogicOperandEnum.AND, 
                value: this.contentPipe.transform('ai-assessments.logicAllRules')  
            },
        ];
    }

    checkQuestionLogic(
        instance: AssessmentPage,
        templateSectionId: AssessmentResourceId,
        templateControlId: AssessmentResourceId,
        preParsedLogic?: AssessmentLogic,
        hideWhenSourceMissing = false): boolean {
            if (!instance.logic) return true;
            const conditionalLogic = preParsedLogic ?? this.parseAiAssessmentLogic(instance.sections, instance.logic, hideWhenSourceMissing);
            
            if (!conditionalLogic || !Object.keys(conditionalLogic)?.length) return true;
            
            const questionKey = this.getSectionControlCombinedId(templateSectionId, templateControlId);
            
            if (questionKey in conditionalLogic) {
                const conditionalLogicResults: boolean[] = [];
                const questionLogic = conditionalLogic[questionKey];
                const operand = questionLogic.operand;
                const sectionIndex = instance.sections.findIndex(section => section.templateSectionId == templateSectionId);
                if (sectionIndex === -1) return true;
                const controlIndex = instance.sections[sectionIndex]?.questions?.findIndex(question => question.templateQuestionId == templateControlId);
                if (controlIndex === -1) return true;

                for (let i=0; i<questionLogic.conditions?.length; i++) {
                    const condition = questionLogic.conditions[i];
                    if (!condition) continue;
                    const questionControl = this.getQuestionById(instance.sections, condition['questionId']) as AssessmentQuestion;

                    if (questionControl && !questionControl.isDeleted) {
                        const questionSectionIndex = instance.sections.findIndex(section => section.id === questionControl.sectionId);
                        if (questionSectionIndex === -1) return true;
                        const questionControlIndex = instance.sections[questionSectionIndex]?.questions?.findIndex(question => question.id === questionControl.id);
                        if (questionControlIndex === -1) return true;

                        if (questionSectionIndex > sectionIndex) {
                            return true;
                        }
                        else if (questionSectionIndex === sectionIndex) {
                            if (questionControlIndex > controlIndex) {
                                return true;
                            }
                        }

                        const questionValue = this.getQuestionValue(questionControl);
                        let conditionValue = condition['questionValue'] ?? [];
                        let checkResult = true;

                        switch (condition['type']) {
                            case AssessmentLogicTypeEnum.HasSome: {
                                checkResult = this.hasSome(questionValue, conditionValue);
                                break;
                            }

                            case AssessmentLogicTypeEnum.HasExactly:
                            default: {
                                checkResult = this.hasExactly(questionValue, conditionValue);
                                break;
                            }
                        }
                        conditionalLogicResults.push(checkResult);
                    }
                    else if (hideWhenSourceMissing) {
                        // Source question is redacted for this collaborator — a condition they can't see
                        // can't be satisfied, so count it as not met instead of skipping it.
                        conditionalLogicResults.push(false);
                    }
                }

                if (!conditionalLogicResults.length) return true;
                if (!operand) {
                    return conditionalLogicResults.some(r => r === true);
                }
                else if (operand) {
                    const result = operand === AssessmentLogicOperandEnum.AND ?
                        conditionalLogicResults.every(r => r === true) :
                        conditionalLogicResults.some(r => r === true);

                    if (!result) return false;
                }
            }
            return true;
    }

    checkSectionLogic(
        instance: AssessmentPage,
        templateSectionId: AssessmentResourceId,
        preParsedLogic?: AssessmentLogic,
        hideWhenSourceMissing = false): boolean {
            if (!instance.logic) return true;
            const conditionalLogic = preParsedLogic ?? this.parseAiAssessmentLogic(instance.sections, instance.logic, hideWhenSourceMissing);
            
            if (!conditionalLogic || !Object.keys(conditionalLogic)?.length) return true;
            
            const questionKey = this.getSectionControlCombinedId(templateSectionId);
            
            if (questionKey in conditionalLogic) {
                const conditionalLogicResults: boolean[] = [];
                const questionLogic = conditionalLogic[questionKey];
                const operand = questionLogic.operand;
                const sectionIndex = instance.sections.findIndex(section => section.templateSectionId == templateSectionId);
                if (sectionIndex === -1) return true;

                for (let i=0; i<questionLogic.conditions?.length; i++) {
                    const condition = questionLogic.conditions[i];
                    if (!condition) continue;
                    const questionControl = this.getQuestionById(instance.sections, condition['questionId']) as AssessmentQuestion;

                    if (questionControl && !questionControl.isDeleted) {
                        const questionSectionIndex = instance.sections.findIndex(section => section.id === questionControl.sectionId);
                        if (questionSectionIndex === -1) return true;
                        const questionControlIndex = instance.sections[questionSectionIndex]?.questions?.findIndex(question => question.id === questionControl.id);
                        if (questionControlIndex === -1) return true;

                        if (questionSectionIndex > sectionIndex) {
                            return true;
                        }

                        const questionValue = this.getQuestionValue(questionControl);
                        let conditionValue = condition['questionValue'] ?? [];
                        let checkResult = true;

                        switch (condition['type']) {
                            case AssessmentLogicTypeEnum.HasSome: {
                                checkResult = this.hasSome(questionValue, conditionValue);
                                break;
                            }

                            case AssessmentLogicTypeEnum.HasExactly:
                            default: {
                                checkResult = this.hasExactly(questionValue, conditionValue);
                                break;
                            }
                        }
                        conditionalLogicResults.push(checkResult);
                    }
                    else if (hideWhenSourceMissing) {
                        // Source question is redacted for this collaborator — count the condition as not
                        // met so the dependent section stays hidden instead of rendering unconditionally
                        // (which would block completion). QA bugs 13339859850 / 13339854204.
                        conditionalLogicResults.push(false);
                    }
                }

                if (!conditionalLogicResults.length) return true;
                if (!operand) {
                    return conditionalLogicResults.some(r => r === true);
                }
                else if (operand) {
                    const result = operand === AssessmentLogicOperandEnum.AND ?
                        conditionalLogicResults.every(r => r === true) :
                        conditionalLogicResults.some(r => r === true);

                    if (!result) return false;
                }
            }
            return true;
    }

    private getQuestionValue(control: AssessmentQuestion): AssessmentResourceValue {
        try {    
            const questionType = this.assessmentsMapperService.getQuestionType({ type: control.type, name: control.name });

            switch (questionType) {
                case AssessmentQuestionTypeEnum.Dropdown: {
                    if (!Array.isArray(control.value)) return [];
                    return (control.value as AssessmentDropdownOption[]).filter(option => !!option?.isSelected).map(option => option?.label) as AssessmentResourceValue;
                }
                case AssessmentQuestionTypeEnum.BusinessUnit:
                case AssessmentQuestionTypeEnum.DataSubjects: 
                case AssessmentQuestionTypeEnum.LegalBasis:
                case AssessmentQuestionTypeEnum.TransferMechanism: {
                    return (control.value as AssessmentEntity[])?.map(entity =>
                        entity.isDefault ? entity.id : this.resolveEntityName(entity.id as string)
                    ) as AssessmentResourceValue;
                }
                case AssessmentQuestionTypeEnum.DataSources: {
                    const systemsQuery = this.injector.get(SystemsQuery);
                    return (control.value as AssessmentResourceId[])?.map(id => systemsQuery.getEntity(''+id)?.name ?? id) as AssessmentResourceValue;
                }
                case AssessmentQuestionTypeEnum.DpiaNeeded: {
                    return [this.contentPipe.transform(`ropa.${control.value ? 'dpiaEnable' : 'dpiaDisable'}`)];
                }
                case AssessmentQuestionTypeEnum.PartiesWithAccess: {
                    return (control.value as PartiesWithAccess)?.type as AssessmentResourceValue;
                }
                case AssessmentQuestionTypeEnum.Risks: {
                    return (control.value as Risk[])?.map(risk => risk?.name) as AssessmentResourceValue;
                }
                case AssessmentQuestionTypeEnum.DropdownCustomField: {
                    return (control.value as AssessmentCustomFieldDropdown)
                        ?.options?.filter(option => !!option?.isSelected)
                        ?.map(option => option?.label) as AssessmentResourceValue;
                }
                case AssessmentQuestionTypeEnum.Textarea: {
                    return (control.value as AssessmentCustomFieldText)?.textValue as AssessmentResourceValue;
                }
                case AssessmentQuestionTypeEnum.DataRole: {
                    return (control.value as string[]);
                }
                case AssessmentQuestionTypeEnum.DataTypes: {
                    return (control.value as AssessmentDataTypeSource[])
                        ?.map(entity => entity?.dataType?.isDefault
                            ? entity?.dataType?.id
                            : this.resolveEntityName(entity?.dataType?.id as string)
                        ) as AssessmentResourceValue;
                }
                default: {
                    return control.value as AssessmentResourceValue;
                }
            }
        }
        catch (error) {
            console.error(error);
            return [];
        }
    }

    hasExactly(questionValues: AssessmentResourceValue, targetValues: AssessmentResourceValue): boolean {
        const qValues = this.arrayToLowerCase(questionValues);
        const tValues = this.arrayToLowerCase(targetValues);
        return qValues?.every(value => tValues?.includes(value)) && qValues?.length === tValues?.length;
    }

    hasSome(questionValues: AssessmentResourceValue, targetValues: AssessmentResourceValue): boolean {
        const qValues = this.arrayToLowerCase(questionValues);
        const tValues = this.arrayToLowerCase(targetValues);
        return qValues?.some(value => tValues?.includes(value));
    }

    /**
     * Converts input values to an array of lowercase, trimmed, alphanumeric-only strings.
     * Removes all non-alphanumeric characters.
     */
    private arrayToLowerCase(values: AssessmentResourceValue): string[] {
        const sanitize = (val: unknown): string =>
            val?.toString()
                .toLowerCase()
                .trim()
                .replace(/[^a-z0-9]/gi, '');

        if (Array.isArray(values)) {
            return values
                .filter(Boolean)
                .map(sanitize)
                .filter(Boolean);
        }
        return [sanitize(values)];
    }

    onRemoveDropdownOption(
        currentLogic: AssessmentLogic, 
        sections: AssessmentSection[] | AssessmentsMineOSFormControl[], 
        sectionId: AssessmentResourceId, 
        controlId: AssessmentResourceId, 
        optionIndex: number
    ): AssessmentLogic {
        if (!currentLogic || !Object.keys(currentLogic)?.length || !sections?.length || !sectionId || !controlId || !this.isValidIndex(optionIndex)) return;
        try {
            const section = sections.find(sec => sec.id === sectionId);
            const control = this.questionTypeHelper.getSectionQuestions(section)?.find(ctrl => ctrl.id === controlId);
            const optionToRemove = control?.value?.[optionIndex]; 
            
            if (!optionToRemove?.value) {
                return;
            }

            for (const key in currentLogic) {
                for (let i=0; i<currentLogic[key]?.conditions?.length; i++) {
                    const questionLogic = currentLogic[key].conditions[i];
                    if (!!questionLogic && questionLogic['questionValue']?.includes(optionToRemove.value)) {
                        questionLogic['questionValue'] = questionLogic['questionValue'].filter(option => option !== optionToRemove.value);
                        
                        // in case it was the only option selected remove condition
                        if (!questionLogic['questionValue'].length) {
                            currentLogic[key].conditions.splice(i, 1);
                            if (!currentLogic[key].conditions?.length) {
                                delete currentLogic[key];
                            }
                        }
                    }
                }
            }
            return;
        }
        catch (err) {
            console.error('Could not parse instance logic');
        }
    }

    getSectionControlCombinedId(sectionId: AssessmentResourceId, controlId?: AssessmentResourceId): string {
        if (!sectionId && !controlId) return '';
        if (!controlId) return `${sectionId}`;
        return `${sectionId}${this.idDelimiter}${controlId}`;
    }

    // formControlIds of questions that are a *source* of at least one conditional-logic rule
    // (some rule's condition depends on their value). A value change on any control NOT in this
    // set can never change a question's visibility, so the conditional-logic cache does not need
    // rebuilding for it. Empty set when the assessment has no logic. Parses the logic once.
    // Callers should cache the result and refresh it only on structure change.
    getLogicSourceFormControlIds(instance: AssessmentPage): Set<string> {
        const result = new Set<string>();
        if (!instance?.logic || !instance.sections?.length) return result;

        const parsedLogic = this.parseAiAssessmentLogic(instance.sections, instance.logic);
        if (!Object.keys(parsedLogic ?? {}).length) return result;

        // Combined `sectionId||controlId` keys referenced as a source in any rule's conditions.
        const sourceQuestionIds = new Set<string>();
        for (const questionKey of Object.keys(parsedLogic)) {
            for (const condition of parsedLogic[questionKey]?.conditions ?? []) {
                const sourceId = condition?.['questionId'];
                if (sourceId) sourceQuestionIds.add(`${sourceId}`);
            }
        }

        // Map those source keys to their form controls in a single section pass.
        for (const section of instance.sections) {
            for (const question of section.questions ?? []) {
                const combinedId = this.getSectionControlCombinedId(section.templateSectionId, question.templateQuestionId);
                if (sourceQuestionIds.has(combinedId) && question.formControlId != null) {
                    result.add(`${question.formControlId}`);
                }
            }
        }
        return result;
    }

    private isValidIndex(index: number | null | undefined): index is number {
        return index !== null && index !== undefined;
    }

    stringifyAiAssessmentLogic(currentLogic: AssessmentLogic): string {
        const clonedLogic = _.cloneDeep(this.cleanBeforeSubmit(currentLogic));
        return JSON.stringify(clonedLogic);
    }

    // Make adjustments to the current logic before sending to BE.
    // Move from reactive form control types to AiAssessmentLogic interface
    private cleanBeforeSubmit(currentLogic: AssessmentLogic): AssessmentLogic {
        if (!currentLogic || !Object.keys(currentLogic)?.length) return {};
        
        Object.keys(currentLogic).forEach(questionId => {
            if (!!currentLogic[questionId] && (!questionId || questionId === "undefined")) {
                delete currentLogic[questionId];
                return;
            }
            const questionLogic = currentLogic[questionId];
            
            // Store the operand before processing conditions
            const operand = questionLogic?.operand;
            
            // Filter out invalid conditions instead of deleting the entire logic entry
            if (!!currentLogic[questionId] && questionLogic?.conditions?.length) {
                const validConditions = questionLogic.conditions.filter(condition => 
                    condition && condition?.['questionId'] && condition?.['questionValue']
                );
                
                // Only delete the entire logic entry if there are no valid conditions
                if (validConditions.length === 0) {
                    delete currentLogic[questionId];
                    return;
                } else {
                    // Update the conditions array with only valid conditions
                    currentLogic[questionId].conditions = validConditions as any;
                }
            } else if (!!currentLogic[questionId] && !questionLogic?.conditions?.length) {
                // Delete if there are no conditions at all
                delete currentLogic[questionId];
                return;
            }
            
            // Handle operand - ensure it's preserved if it exists and the logic entry still exists
            if (!!currentLogic[questionId]) {
                if (operand && (operand === AssessmentLogicOperandEnum.AND || operand === AssessmentLogicOperandEnum.OR)) {
                    // Keep the operand as is if it's a valid enum value
                    currentLogic[questionId].operand = operand;
                } else {
                    // Set default operand to OR if no operand is specified
                    currentLogic[questionId].operand = AssessmentLogicOperandEnum.OR;
                }
            }

            // Process the remaining valid conditions
            for (let i=0; i<currentLogic[questionId]?.conditions?.length; i++) {
                const condition = currentLogic[questionId].conditions[i];
                if (!!currentLogic[questionId] && !condition?.['type']) {
                    currentLogic[questionId].conditions[i]['type'] = AssessmentLogicTypeEnum.HasExactly;
                }
                if (!!currentLogic[questionId] && this.isDropdownOption(condition['type'])) {
                    currentLogic[questionId].conditions[i]['type'] = condition['type'].id;
                }
                if (!!currentLogic[questionId] && this.isDropdownOption(condition['questionId'])) {
                    currentLogic[questionId].conditions[i]['questionId'] = condition['questionId'].id;
                }
                if (!!currentLogic[questionId] && this.isDropdownOption(condition['questionValue'])) {
                    currentLogic[questionId].conditions[i]['questionValue'] = [condition['questionValue'].value];
                }
            }
        });

        return currentLogic;
    }

    isDropdownOption(option: DropdownOption | string): boolean {
        return typeof option === 'object' && option !== null && 'id' in option;
    }
    
    /**
     * @param keepUnresolvedConditions when true (limited-collaborator surface), a condition whose
     * source question is not present in `sections` is KEPT instead of being dropped. The source is a
     * question in a section redacted for this collaborator, so the rule must still be evaluated — as
     * "not met" (see `checkSectionLogic`/`checkQuestionLogic` `hideWhenSourceMissing`) — rather than
     * stripped, which would leave the dependent shown unconditionally. Agents pass false (default):
     * missing-source conditions are dropped exactly as before. (QA bugs 13339859850 / 13339854204.)
     */
    parseAiAssessmentLogic(sections: AssessmentSection[] | AssessmentsMineOSFormControl[], logic: string, keepUnresolvedConditions = false): AssessmentLogic {
        if (!sections?.length || !logic) return {};
        try {
            // remove conditions on null reference questions
            let parsedLogic = JSON.parse(logic);

            // Heal section ids persisted under a different scheme than the logic keys. The template's
            // conditional logic is keyed on the deterministic `generateSectionUuid` (`group-<name>`),
            // and the canonical (manual) path stamps that same uuid onto `templateSectionId`. The
            // Typeform/integration generator (assessment-generator-v4) instead stamps the section's
            // numeric template id, so every section-level (and section-scoped question) rule fails to
            // match and the section renders unconditionally. Re-key by matching each section's
            // recomputed deterministic uuid back to its actual id. No-op when they already agree.
            parsedLogic = this.healStaleSectionIds(sections, parsedLogic);

            // Heal stale top-level keys the same way conditions are healed below: when a dependent
            // question moves to another section its `sectionId||controlId` key goes stale — re-key it
            // by the still-unique control id instead of dropping the rule. Without this, moving a
            // dynamic question silently loses its logic (it stays shown regardless of its source).
            // Genuinely-removed questions find no match and fall through to the deletion below.
            for (const key of Object.keys(parsedLogic)) {
                if (this.isValidQuestionId(sections, key) || !key.includes(this.idDelimiter)) {
                    continue;
                }
                const remappedKey = this.findQuestionKeyByControlId(sections, key.split(this.idDelimiter)[1]);
                if (remappedKey && remappedKey !== key && !parsedLogic[remappedKey]) {
                    parsedLogic[remappedKey] = parsedLogic[key];
                    delete parsedLogic[key];
                }
            }

            Object.keys(parsedLogic).forEach(questionId => {
                if (!this.isValidQuestionId(sections, questionId)) {
                    delete parsedLogic[questionId];
                }
                else {
                    // Preserve the operand if it exists
                    const operand = parsedLogic[questionId].operand;

                    parsedLogic[questionId].conditions = (parsedLogic[questionId].conditions ?? [])
                        // Heal stale section ids by remapping on the still-unique control id.
                        .map(condition => this.remapConditionQuestionId(sections, condition))
                        // Keep conditions whose source is missing when keepUnresolvedConditions is set
                        // (collaborator): the source lives in a redacted section and the rule must still
                        // evaluate to "not met". Otherwise drop unresolved conditions as before.
                        .filter(condition => !!condition?.['questionId'] && (keepUnresolvedConditions || this.isValidQuestionId(sections, condition['questionId'])));

                    // Drop entries left with no conditions — a valid key with an empty array renders a dead panel.
                    if (!parsedLogic[questionId].conditions.length) {
                        delete parsedLogic[questionId];
                        return;
                    }

                    // Ensure operand is preserved after filtering conditions, default to OR if none exists
                    if (operand && (operand === AssessmentLogicOperandEnum.AND || operand === AssessmentLogicOperandEnum.OR)) {
                        parsedLogic[questionId].operand = operand;
                    } else {
                        // Set default operand to OR if no operand is specified
                        parsedLogic[questionId].operand = AssessmentLogicOperandEnum.OR;
                    }
                }
            });
            return parsedLogic;
        }
        catch (err) {
            console.error('Could not parse instance logic');
            return {};
        }
    }

    // Re-key logic whose section references use the deterministic `generateSectionUuid` while the
    // instance persisted a different section id (e.g. the numeric template id from the
    // Typeform/integration path). Builds a `deterministic-uuid -> actual-section-id` map from the
    // live sections and rewrites both section-level keys and the section segment of every
    // `sectionId||controlId` reference. Sections whose id already equals their deterministic uuid
    // (the canonical/manual path) are skipped, so correct assessments pass through untouched.
    private healStaleSectionIds(
        sections: AssessmentSection[] | AssessmentsMineOSFormControl[],
        parsedLogic: AssessmentLogic
    ): AssessmentLogic {
        const deterministicIdToSectionId = new Map<string, string>();
        for (const section of sections) {
            const sectionId = 'templateSectionId' in section ? (section as AssessmentSection).templateSectionId : section.id;
            const name = (section as AssessmentSection | AssessmentsMineOSFormControl).name;
            if (sectionId == null || !name) continue;
            // Route through the canonical generator (single source of the `type-name` seed) with a
            // synthetic id-less shape — a real section carries an id, which generateSectionUuid would
            // return verbatim. Sections are always `group`-typed; instances may omit `type`, so default it.
            const deterministicId = coreGenerateSectionUuid(
                { name, type: (section as { type?: string }).type ?? 'group' } as unknown as CoreFormControl
            );
            if (deterministicId && `${deterministicId}` !== `${sectionId}`) {
                deterministicIdToSectionId.set(`${deterministicId}`, `${sectionId}`);
            }
        }
        if (!deterministicIdToSectionId.size) return parsedLogic;

        const remapSectionSegment = (id: string): string => {
            if (typeof id !== 'string' || !id) return id;
            if (id.includes(this.idDelimiter)) {
                // Control ids are opaque — split on the FIRST delimiter only and preserve the rest.
                const [sectionPart, ...rest] = id.split(this.idDelimiter);
                const mapped = deterministicIdToSectionId.get(sectionPart);
                return mapped ? this.getSectionControlCombinedId(mapped, rest.join(this.idDelimiter)) : id;
            }
            return deterministicIdToSectionId.get(id) ?? id;
        };

        // Remap immutably (never mutate the parsed entries/conditions in place). Keys that don't
        // change are "genuine" and claimed first; remapped keys are applied afterwards and only
        // where a genuine entry hasn't already taken the slot, so genuine always wins on collision
        // regardless of iteration order.
        const healed: AssessmentLogic = {} as AssessmentLogic;
        const remappedEntries: Array<[string, AssessmentLogicConditions]> = [];
        for (const key of Object.keys(parsedLogic)) {
            const entry = parsedLogic[key];
            const conditions = (entry?.conditions ?? []).map(condition => {
                const questionId = condition?.['questionId'];
                return typeof questionId === 'string'
                    ? { ...condition, questionId: remapSectionSegment(questionId) }
                    : condition;
            });
            const healedEntry = { ...entry, conditions } as AssessmentLogicConditions;
            const remappedKey = remapSectionSegment(key);
            if (remappedKey === key) {
                healed[key] = healedEntry;
            } else {
                remappedEntries.push([remappedKey, healedEntry]);
            }
        }
        for (const [key, entry] of remappedEntries) {
            if (!(key in healed)) healed[key] = entry;
        }
        return healed;
    }

    // Remap a condition's stale `sectionId||controlId` reference to the correct key by its unique control id.
    private remapConditionQuestionId(
        sections: AssessmentSection[] | AssessmentsMineOSFormControl[],
        condition: AssessmentLogicCondition
    ): AssessmentLogicCondition {
        const questionId = condition?.['questionId'];
        if (!questionId || typeof questionId !== 'string' || this.isValidQuestionId(sections, questionId)) {
            return condition;
        }
        // Only combined section||control references can be healed by control id.
        if (!questionId.includes(this.idDelimiter)) {
            return condition;
        }
        const controlId = questionId.split(this.idDelimiter)[1];
        const remappedKey = this.findQuestionKeyByControlId(sections, controlId);
        return remappedKey ? { ...condition, questionId: remappedKey } : condition;
    }

    private findQuestionKeyByControlId(
        sections: AssessmentSection[] | AssessmentsMineOSFormControl[],
        controlId: string
    ): string | null {
        if (!controlId) return null;
        const matches: string[] = [];
        for (const section of sections) {
            const questions = this.questionTypeHelper.getSectionQuestions(section);
            for (const control of (questions ?? [])) {
                const sectionId = 'templateSectionId' in section ? (section as AssessmentSection).templateSectionId : section.id;
                const ctrlId = 'templateQuestionId' in control ? (control as AssessmentQuestion).templateQuestionId : control.id;
                if (ctrlId === controlId) {
                    matches.push(this.getSectionControlCombinedId(sectionId, ctrlId));
                }
            }
        }
        // Only heal when the control id maps to exactly one question across all sections.
        return matches.length === 1 ? matches[0] : null;
    }

    isValidConditionalLogic(currentLogic: AssessmentLogic): boolean {
        if (!currentLogic || !Object.keys(currentLogic)?.length) return true;
        for (let questionId of Object.keys(currentLogic)) {
            if (currentLogic[questionId] && (!questionId || questionId === "undefined")) {
                return false;
            }
            const questionLogic = currentLogic[questionId];
            if (!questionLogic?.conditions?.length) {
                return false;
            }

            for (let i=0; i<questionLogic?.conditions?.length; i++) {
                const condition = questionLogic.conditions[i];
                if (!condition || !condition?.['questionId'] || !condition?.['questionValue']) {
                    return false;
                }
                if (condition && this.isDropdownOption(condition['questionValue']) && !Object.keys(condition['questionValue'])?.length) {
                    return false;
                }
                if (condition && Array.isArray(condition['questionValue']) && !condition['questionValue'].length) {
                    return false;
                }
            } 
        }
        return true;
    }

    checkQuestionLogicForTemplate(
        conditionalLogic: AssessmentLogic,
        sections: AssessmentsMineOSFormControl[],
        sectionId: AssessmentResourceId,
        controlId: AssessmentResourceId
    ): boolean {
        if (!conditionalLogic || !Object.keys(conditionalLogic)?.length) return true;
        
        const questionKey = this.getSectionControlCombinedId(sectionId, controlId);
        
        if (questionKey in conditionalLogic) {
            const conditionalLogicResults: boolean[] = [];
            const questionLogic = conditionalLogic[questionKey];
            const operand = questionLogic.operand;
            const sectionIndex = sections.findIndex(section => section.id == sectionId);
            if (sectionIndex === -1) return true;
            const section = sections[sectionIndex];
            const questions = this.questionTypeHelper.getSectionQuestions(section);
            const controlIndex = questions?.findIndex(control => control.id == controlId);
            if (controlIndex === -1) return true;

            for (let i=0; i<questionLogic.conditions?.length; i++) {
                const condition = questionLogic.conditions[i];
                if (!condition) continue;
                const questionControl = this.getQuestionById(sections, condition['questionId']) as AssessmentsMineOSFormControl;

                if (questionControl) {
                    const questionSectionIndex = sections.findIndex(sec => sec.id === questionControl['sectionId']);
                    if (questionSectionIndex === -1) return true;
                    const questionSection = sections[questionSectionIndex];
                    const questionControlIndex = this.questionTypeHelper.getSectionQuestions(questionSection)?.findIndex(ctrl => ctrl.id === questionControl.id);
                    if (questionControlIndex === -1) return true;

                    if (questionSectionIndex > sectionIndex) {
                        return true;
                    }
                    else if (questionSectionIndex === sectionIndex) {
                        if (questionControlIndex > controlIndex) {
                            return true;
                        }
                    }

                    const questionValue = this.getTemplateQuestionValue(questionControl);
                    let conditionValue = condition['questionValue'] ?? [];
                    let checkResult = true;

                    switch (condition['type']) {
                        case AssessmentLogicTypeEnum.HasSome: {
                            checkResult = this.hasSome(questionValue, conditionValue);
                            break;
                        }
    
                        case AssessmentLogicTypeEnum.HasExactly:
                        default: {
                            checkResult = this.hasExactly(questionValue, conditionValue);
                            break;
                        }
                    }
                    conditionalLogicResults.push(checkResult);
                }
            }

            if (!conditionalLogicResults.length) return true;
            if (!operand) {
                return conditionalLogicResults.some(r => r === true);
            }
            else if (operand) {
                const result = operand === AssessmentLogicOperandEnum.AND ? 
                    conditionalLogicResults.every(r => r === true) :
                    conditionalLogicResults.some(r => r === true);
                    
                if (!result) return false;
            }
        }
        return true;
    }

    checkSectionLogicForTemplate(
        conditionalLogic: AssessmentLogic,
        sections: AssessmentsMineOSFormControl[],
        sectionId: AssessmentResourceId
    ): boolean {
        if (!conditionalLogic || !Object.keys(conditionalLogic)?.length) return true;
        
        const questionKey = this.getSectionControlCombinedId(sectionId);
        
        if (questionKey in conditionalLogic) {
            const conditionalLogicResults: boolean[] = [];
            const questionLogic = conditionalLogic[questionKey];
            const operand = questionLogic.operand;
            const sectionIndex = sections.findIndex(section => section.id == sectionId);
            if (sectionIndex === -1) return true;

            for (let i=0; i<questionLogic.conditions?.length; i++) {
                const condition = questionLogic.conditions[i];
                if (!condition) continue;
                const questionControl = this.getQuestionById(sections, condition['questionId']) as AssessmentsMineOSFormControl;

                if (questionControl) {
                    const questionSectionIndex = sections.findIndex(sec => sec.id === questionControl['sectionId']);
                    if (questionSectionIndex === -1) return true;
                    const questionSection = sections[questionSectionIndex];
                    const questionControlIndex = this.questionTypeHelper.getSectionQuestions(questionSection)?.findIndex(ctrl => ctrl.id === questionControl.id);
                    if (questionControlIndex === -1) return true;

                    if (questionSectionIndex > sectionIndex) {
                        return true;
                    }

                    const questionValue = this.getTemplateQuestionValue(questionControl);
                    let conditionValue = condition['questionValue'] ?? [];
                    let checkResult = true;

                    switch (condition['type']) {
                        case AssessmentLogicTypeEnum.HasSome: {
                            checkResult = this.hasSome(questionValue, conditionValue);
                            break;
                        }
    
                        case AssessmentLogicTypeEnum.HasExactly:
                        default: {
                            checkResult = this.hasExactly(questionValue, conditionValue);
                            break;
                        }
                    }
                    conditionalLogicResults.push(checkResult);
                }
            }

            if (!conditionalLogicResults.length) return true;
            if (!operand) {
                return conditionalLogicResults.some(r => r === true);
            }
            else if (operand) {
                const result = operand === AssessmentLogicOperandEnum.AND ? 
                    conditionalLogicResults.every(r => r === true) :
                    conditionalLogicResults.some(r => r === true);
                    
                if (!result) return false;
            }
        }
        return true;
    }

    private getTemplateQuestionValue(control: AssessmentsMineOSFormControl): AssessmentResourceValue {
        try {    
            const questionType = this.assessmentsMapperService.getQuestionType({ type: control.type, name: control.name });

            switch (questionType) {
                case AssessmentQuestionTypeEnum.Dropdown:
                case AssessmentQuestionTypeEnum.DropdownCustomField: {
                    // Check if value is an array of dropdown options
                    if (Array.isArray(control.value) && control.value.length > 0) {
                        const firstValue = control.value[0] as any;
                        // Type guard to check if this is an array of dropdown options
                        if (firstValue && typeof firstValue === 'object' && 'isSelected' in firstValue) {
                            return (control.value as unknown as AssessmentDropdownOption[])?.filter(option => !!option?.isSelected)?.map(option => option?.label) as AssessmentResourceValue;
                        }
                    }
                    return [];
                }
                default: {
                    // For template forms, most controls won't have actual values yet
                    // They're being built/configured, not filled out
                    return control.value as AssessmentResourceValue || [];
                }
            }
        }
        catch (error) {
            console.error(error);
            return [];
        }
    }

    private isControlTypeAndNameIsLogicSupported(control: AssessmentQuestion | AssessmentsMineOSFormControl): boolean {
        const questionType = this.assessmentsMapperService.getQuestionType({ type: control.type, name: control.name });
        
        return [
            AssessmentQuestionTypeEnum.Dropdown,
            AssessmentQuestionTypeEnum.DropdownCustomField,
            AssessmentQuestionTypeEnum.BusinessUnit,
            AssessmentQuestionTypeEnum.DataRole,
            AssessmentQuestionTypeEnum.DataSources,
            AssessmentQuestionTypeEnum.DataSubjects,
            AssessmentQuestionTypeEnum.DataTypes,
            AssessmentQuestionTypeEnum.DpiaNeeded,
            AssessmentQuestionTypeEnum.LegalBasis,
            AssessmentQuestionTypeEnum.PartiesWithAccess,
            AssessmentQuestionTypeEnum.Risks,
            AssessmentQuestionTypeEnum.TransferMechanism
        ].includes(questionType as AssessmentQuestionTypeEnum);
    }
}